import { LOAD_CAPACITY, fmt } from "../lib/calc";
import { useDropZone } from "../context/DragContext";
import { printLoadSheet, downloadLoadCsv, downloadAllLoadsCsv, downloadDeliveryScheduleCsv } from "../lib/bookingSheet";
import { Truck, Plus, X, Printer, FileDown, Boxes, RotateCcw, ClipboardList } from "lucide-react";

export function LoadPlanner({ panelsById, loads, projectName = "", onAssign, onUnassign, onAddLoad, onAutoPack, onResetLoads }) {
  const hasAny = loads.some((l) => (l.panelIds || []).length > 0);
  return (
    <div className="bg-[#16181A] border border-[#2E3033]">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#2E3033] gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Truck className="w-4 h-4 text-[#2560C9]" />
          <h2 className="font-heading font-bold text-white text-sm tracking-tight">LOAD PLANNER</h2>
          <span className="text-[10px] font-mono text-[#6E7787] ml-1">
            {loads.length} loads · {LOAD_CAPACITY} m³ each
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            data-testid="auto-pack-button"
            onClick={() => onAutoPack()}
            className="flex items-center gap-1 bg-[#2560C9] hover:bg-[#3B75DF] text-white text-xs px-2.5 py-1.5 transition-colors"
            title="Automatically fill loads from unplaced panels (≤ 6.8 m³ each)"
          >
            <Boxes className="w-3.5 h-3.5" /> Auto-Pack
          </button>
          <button
            data-testid="reset-loads-button"
            onClick={onResetLoads}
            disabled={!hasAny}
            className="flex items-center gap-1 border border-[#2E3033] text-[#A0AABF] hover:border-[#EF4444]/60 hover:text-[#EF4444] text-xs px-2.5 py-1.5 transition-colors disabled:opacity-40"
            title="Clear all load assignments"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Reset
          </button>
          <button
            data-testid="export-all-loads-button"
            onClick={() => downloadAllLoadsCsv(loads, panelsById, projectName)}
            disabled={!hasAny}
            className="flex items-center gap-1 border border-[#2E3033] hover:bg-[#22252A] text-white text-xs px-2.5 py-1.5 transition-colors disabled:opacity-50"
            title="Export all loads (panel-level) as CSV"
          >
            <FileDown className="w-3.5 h-3.5" /> Export CSV
          </button>
          <button
            data-testid="delivery-schedule-button"
            onClick={() => downloadDeliveryScheduleCsv(loads, panelsById, projectName)}
            disabled={!hasAny}
            className="flex items-center gap-1 border border-[#2E3033] hover:bg-[#22252A] text-white text-xs px-2.5 py-1.5 transition-colors disabled:opacity-50"
            title="Export delivery schedule (one row per load, in order)"
          >
            <ClipboardList className="w-3.5 h-3.5" /> Delivery CSV
          </button>
          <button
            data-testid="add-load-button"
            onClick={onAddLoad}
            className="flex items-center gap-1 border border-[#2E3033] hover:bg-[#22252A] text-white text-xs px-2.5 py-1.5 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Add Load
          </button>
        </div>
      </div>

      <div data-testid="load-planner-grid" className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {loads.map((load) => (
          <LoadCard key={load.id} load={load} panelsById={panelsById} projectName={projectName} onAssign={onAssign} onUnassign={onUnassign} />
        ))}
      </div>
    </div>
  );
}

function LoadCard({ load, panelsById, projectName = "", onAssign, onUnassign }) {
  const panels = load.panelIds.map((id) => panelsById[id]).filter(Boolean);
  const volume = panels.reduce((s, p) => s + (p.volume || 0), 0);
  const pct = Math.min((volume / LOAD_CAPACITY) * 100, 100);
  const over = volume > LOAD_CAPACITY;
  const barColor = over ? "#FF3B30" : pct > 85 ? "#F59E0B" : "#2560C9";
  const strengths = [...new Set(panels.map((p) => p.strength28).filter((x) => x != null && x > 0))];
  const mixed = strengths.length > 1;

  const attach = useDropZone((payload) => {
    if (payload?.panelId) onAssign(load.id, payload.panelId);
  });

  return (
    <div
      ref={attach}
      data-testid={`load-card-${load.id}`}
      className="bg-[#0A0A0A] border border-[#2E3033] hover:border-[#2560C9]/50 transition-colors p-3 flex flex-col"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-heading font-bold text-white text-sm tracking-tight">{load.label}</span>
          {strengths.length > 0 && (
            <span
              data-testid={`load-mpa-${load.id}`}
              title={mixed ? "This load mixes different concrete strengths" : "28-day strength for this load"}
              className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 border"
              style={mixed
                ? { color: "#F59E0B", borderColor: "#F59E0B66", backgroundColor: "#F59E0B14" }
                : { color: "#3B75DF", borderColor: "#2560C966" }}
            >
              {mixed ? "Mixed MPa" : `${strengths[0]} MPa`}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {panels.length > 0 && (
            <>
              <button
                data-testid={`print-load-${load.id}`}
                onClick={() => printLoadSheet(load, panelsById, projectName)}
                className="text-[#6E7787] hover:text-[#2560C9] transition-colors"
                title="Print booking sheet"
              >
                <Printer className="w-3.5 h-3.5" />
              </button>
              <button
                data-testid={`csv-load-${load.id}`}
                onClick={() => downloadLoadCsv(load, panelsById, projectName)}
                className="text-[#6E7787] hover:text-[#2560C9] transition-colors"
                title="Download CSV"
              >
                <FileDown className="w-3.5 h-3.5" />
              </button>
            </>
          )}
          <span
            data-testid={`load-volume-${load.id}`}
            className="text-[11px] font-mono"
            style={{ color: over ? "#FF3B30" : "#A0AABF" }}
          >
            {fmt(volume, 2)} / {LOAD_CAPACITY} m³
          </span>
        </div>
      </div>

      <div className="mt-2 h-1.5 bg-[#22252A] w-full">
        <div className="h-full transition-all duration-300" style={{ width: `${pct}%`, backgroundColor: barColor }} />
      </div>
      {over && <p className="text-[9px] font-mono uppercase tracking-wider text-[#FF3B30] mt-1">Over capacity</p>}

      <div className="mt-2 space-y-1 min-h-[42px]">
        {panels.length === 0 ? (
          <div className="text-[10px] font-mono text-[#6E7787] border border-dashed border-[#2E3033] py-3 text-center">
            Drop panels here
          </div>
        ) : (
          panels.map((p, idx) => (
            <div
              key={`${load.id}-${p.id}-${idx}`}
              className="flex items-center justify-between bg-[#16181A] border border-[#2E3033] px-2 py-1"
            >
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="w-2 h-2 shrink-0" style={{ backgroundColor: p.color }} />
                <span className="text-[11px] font-mono text-white truncate">{p.name}</span>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <span className="text-[10px] font-mono text-[#6E7787]">{fmt(p.volume, 2)} m³</span>
                <button
                  data-testid={`unassign-${load.id}-${p.id}`}
                  onClick={() => onUnassign(load.id, p.id)}
                  className="text-[#6E7787] hover:text-[#EF4444]"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
